import type { Locale } from './index';
import { getLocale } from './index';
import type { LocalizedString } from '../turn/types';

const warned = new Set<string>();

function opposite(locale: Locale): Locale {
  return locale === 'pl' ? 'en' : 'pl';
}

/**
 * Resolve a LocalizedString to a plain string in the active locale.
 * Falls back to the opposite locale if the active one is missing.
 * Returns "" for undefined or empty objects (never throws).
 */
export function localize(
  value: LocalizedString | undefined,
  locale?: Locale,
  fallback?: Locale,
): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;

  const active: Locale = locale ?? getLocale();
  const fb: Locale = fallback ?? opposite(active);

  const primary = value[active];
  if (primary !== undefined) return primary;

  const secondary = value[fb];
  if (secondary !== undefined) {
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      const key = `${active}:${secondary}`;
      if (!warned.has(key)) {
        warned.add(key);
        console.warn(`[localize] Missing '${active}' translation; using '${fb}' fallback: ${JSON.stringify(value)}`);
      }
    }
    return secondary;
  }

  for (const v of Object.values(value)) {
    if (typeof v === 'string') return v;
  }
  return '';
}
